interface TraceError {
  tag: "traceError";
  message: string;
  trace: string;
}

interface ValidationError {
  tag: "validationError";
  message: string;
  errors: string[];
  trace: string;
}

interface ValueError {
  tag: "valueError";
  message: string;
  value: string;
}

interface UnknownError {
  tag: "unknownError";
  message: string;
}

export type AppError = TraceError | ValueError | UnknownError | ValidationError;

interface Error {
  error: AppError;
  readonly success: false;
}

interface Ok<R> {
  value: R;
  readonly success: true;
}
interface ResultOps<T> {
  // Object.defineProperties happens at runtime, so TypeScript can't narrow the type at compile time without a little help.
  // So we define these two type guards to help it.
  isOk(): this is Ok<T>;
  isError(): this is Error;
  get(): T | null;
  orElse(fallback: T): T;
  getOrElse(handler: (e: AppError) => void): T | void;
  map<R>(f: (value: T) => R): Result<R>;
  match<R>(onError: (value: AppError) => R, onOk: (value: T) => R): R;
  forEach(f: (value: T) => void): void;
  traversePromise<R>(f: (value: T) => Promise<R>): Promise<Result<R>>;
}

type Result<T> = (Error | Ok<T>) & ResultOps<T>;

const resultPrototype = {
  isOk<T>(this: Result<T>): this is Ok<T> {
    return this.success;
  },
  isError<T>(this: Result<T>): this is Error {
    return !this.isOk();
  },
  get<T>(this: Result<T>): T | null {
    return this.isOk() ? this.value : null;
  },
  orElse<T>(this: Result<T>, fallback: T): T {
    return this.isOk() ? this.value : fallback;
  },
  getOrElse<T>(this: Result<T>, handler: (e: AppError) => void): T | void {
    if (this.isOk()) {
      return this.value;
    } else {
      handler(this.error);
    }
  },
  map<T, R>(this: Result<T>, f: (value: T) => R): Result<R> {
    return this.isOk() ? ok(f(this.value)) : error(this.error);
  },
  match<T, R>(this: Result<T>, onError: (value: AppError) => R, onOk: (value: T) => R) {
    return this.isOk() ? onOk(this.value) : onError(this.error);
  },
  forEach<T>(this: Result<T>, f: (value: T) => void) {
    if (this.isOk()) f(this.value);
  },
  async traversePromise<T, R>(this: Result<T>, f: (value: T) => Promise<R>): Promise<Result<R>> {
    const result = this.map(f);
    if (result.isOk()) {
      const value = await result.value;
      return ok(value);
    } else {
      return error(result.error);
    }
  }
};

export function error<R = never>(value: AppError): Result<R> {
  const result = Object.create(resultPrototype);
  Object.defineProperties(result, {
    success: { value: false, writable: false },
    error: { value, writable: false }
  });
  return result;
}

export function ok<T>(value: T): Result<T> {
  const result = Object.create(resultPrototype);
  Object.defineProperties(result, {
    success: { value: true, writable: false },
    value: { value, writable: false }
  });
  return result;
}

export default Result;
